import React, { useState, useEffect, useCallback } from 'react';
import { fetchGoldStockAddedReport } from '../../api/reports/goldStockAddedApi';
import GoldStockAddedReportTable from '../../components/report/GoldStockAddedReportTable';
import { Button, Card, CardContent, Typography, Box } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import GoldStockAddedReportFilter from '../../components/report/GoldStockAddedReportFilter';
import ReportTitle from '../../components/common/ReportTitle';
import { exportToCSV, reportHeaders } from '../../utils/exportUtils';
import PageLoader from '../../components/common/PageLoader';

const DEFAULT_LIMIT = 200;

const initialFilterState = {
  today_only: true,
  limit: DEFAULT_LIMIT
};

const GoldStockAddedReportPage = () => {
  const [filters, setFilters] = useState(initialFilterState);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ total_qty: 0, total_weight: 0, total_pure_weight: 0 });
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const loadData = useCallback(async (appliedFilters = filters) => {
    setLoading(true);
    try {
      const res = await fetchGoldStockAddedReport(appliedFilters);
      setData(res.data.rows);
      setTotals(res.data.totals);
    } catch (err) {
      setData([]);
      setTotals({ total_qty: 0, total_weight: 0, total_pure_weight: 0 });
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    loadData(filters);
  }, [loadData, filters]);

  const handleApplyFilters = (newFilters) => {
    const mergedFilters = {
      ...filters,
      ...newFilters
    };

    if (!mergedFilters.date_from && !mergedFilters.date_to) {
      mergedFilters.today_only = true;
      mergedFilters.limit = DEFAULT_LIMIT;
    } else {
      mergedFilters.today_only = false;
      if (mergedFilters.limit === undefined || mergedFilters.limit === null) {
        mergedFilters.limit = DEFAULT_LIMIT;
      }
    }

    setFilters(mergedFilters);
    setFilterOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.date_from) count++;
    if (filters.date_to) count++;
    if (filters.stock_code) count++;
    if (filters.stock_name) count++;
    if (filters.stock_type) count++;
    if (filters.purity) count++;
    if (filters.color) count++;
    return count;
  };

  const handleExport = () => {
    exportToCSV(data, 'gold_stock_history_report', reportHeaders.goldStockAdded);
  };

  const handleLoadRecent = () => {
    setFilters(initialFilterState);
  };

  const handleLoadAll = () => {
    setFilters(prev => ({
      ...prev,
      today_only: false,
      limit: 0
    }));
  };

  return (
    <div style={{ padding: 24 }}>
      <PageLoader loading={loading} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ReportTitle>Gold Stock History Report</ReportTitle>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />} 
            onClick={() => setFilterOpen(true)}
            sx={{ 
              position: 'relative',
              ...(getActiveFiltersCount() > 0 && {
                backgroundColor: 'primary.light',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.main',
                }
              })
            }}
          >
            Filters
            {getActiveFiltersCount() > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              >
                {getActiveFiltersCount()}
              </Box>
            )}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleLoadRecent}
            disabled={loading}
          >
            Load Recent
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleLoadAll}
            disabled={loading}
          >
            Load All
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ minWidth: 120 }}
          >
            Export CSV
          </Button>
        </Box>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Card sx={{ minWidth: 180, bgcolor: 'primary.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Total Qty</Typography>
            <Typography variant="h6" color="inherit">{totals.total_qty}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, bgcolor: 'info.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Total Weight</Typography>
            <Typography variant="h6" color="inherit">{parseFloat(totals.total_weight).toFixed(3)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, bgcolor: 'warning.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Total Pure Weight</Typography>
            <Typography variant="h6" color="inherit">{parseFloat(totals.total_pure_weight).toFixed(3)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, bgcolor: 'success.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Additions</Typography>
            <Typography variant="h6" color="inherit">{data.filter(item => item.action_type === 'ADD').length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, bgcolor: 'warning.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Updates</Typography>
            <Typography variant="h6" color="inherit">{data.filter(item => item.action_type === 'UPDATE').length}</Typography>
          </CardContent>
        </Card>
      </div>
      <GoldStockAddedReportTable data={data} loading={loading} />
      <GoldStockAddedReportFilter open={filterOpen} onClose={() => setFilterOpen(false)} onApply={handleApplyFilters} initialFilters={filters} />
    </div>
  );
};

export default GoldStockAddedReportPage; 